import { type KeyboardEvent, useEffect, useEffectEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormError } from '@/shared/forms';
import { useTouchPrimary } from '@/shared/lib/media';

import { Button } from './Button';
import styles from './EditableMarkdown.module.css';
import { Markdown } from './Markdown';
import { MarkdownEditor } from './MarkdownEditor';

interface Props {
  /** The saved Markdown (server state). */
  value: string;
  /** Persist the new text. A rejected promise keeps the editor open with the draft. */
  onSave: (next: string) => Promise<unknown>;
  /** Accessible name of the textarea. */
  label: string;
  emptyText: string;
  maxLength?: number;
  /** Open in edit mode straight away (a just-added block). */
  startEditing?: boolean;
  /** `click` (default) or `dblclick` opens the editor from the rendered text. */
  activation?: 'click' | 'dblclick';
  /** Called when editing ends (saved, cancelled or unchanged). */
  onDone?: () => void;
  /** Called with the next editing state (parents that must pause drag-and-drop). */
  onEditingChange?: (editing: boolean) => void;
  /** Always-visible edit button (keyboard / touch). Default true. */
  editButton?: boolean;
  /** Label of the edit button. */
  editLabel: string;
  /** Display only: no click-to-edit, no edit button (a participant who may not edit). */
  readOnly?: boolean;
}

/**
 * Rendered Markdown that edits in place and saves itself.
 *
 * - Clicking the text (not a link, not while text is selected) opens the editor; the edit button
 *   is the keyboard / touch alternative.
 * - Clicking outside or tabbing out saves; nothing is sent when the text did not change.
 * - Escape discards the draft. There is no Save button.
 * - A failed save keeps the editor and the draft, shows the error and offers Retry.
 * - One request at a time (outside click, blur and re-renders cannot double-send).
 * - If the saved text changed on the server while editing (another tab, a restore), the draft is
 *   NOT silently written over it: the user chooses to keep their version or take the latest.
 */
export function EditableMarkdown({
  value,
  onSave,
  label,
  emptyText,
  maxLength,
  startEditing = false,
  activation = 'click',
  onDone,
  onEditingChange,
  editButton = true,
  editLabel,
  readOnly = false,
}: Props) {
  const { t } = useTranslation();
  const touch = useTouchPrimary();
  const [editing, setEditing] = useState(startEditing);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState<unknown>(null);
  const [conflict, setConflict] = useState(false);
  const [pending, setPending] = useState(false);
  const restoreFocus = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const editRef = useRef<HTMLButtonElement>(null);
  const base = useRef(value);
  const busy = useRef(false);

  const open = () => {
    base.current = value;
    setDraft(value);
    setError(null);
    setConflict(false);
    setEditing(true);
    onEditingChange?.(true);
  };

  const finish = (focusEdit: boolean) => {
    setEditing(false);
    setError(null);
    setConflict(false);
    restoreFocus.current = focusEdit;
    onEditingChange?.(false);
    onDone?.();
  };

  const save = async (force = false) => {
    if (busy.current) return;
    if (draft === base.current) {
      finish(false); // unchanged: no request
      return;
    }
    if (!force && value !== base.current && value !== draft) {
      setConflict(true); // someone else changed it meanwhile
      return;
    }
    busy.current = true;
    setPending(true);
    setError(null);
    try {
      await onSave(draft);
      finish(false);
    } catch (reason) {
      setError(reason); // the draft stays
    } finally {
      busy.current = false;
      setPending(false);
    }
  };

  const onOutside = useEffectEvent(() => {
    void save();
  });

  useEffect(() => {
    if (!editing) return;
    const onPointerDown = (event: PointerEvent) => {
      const root = rootRef.current;
      if (!root || (event.target instanceof Node && root.contains(event.target))) return;
      onOutside();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [editing]);

  useEffect(() => {
    if (!editing && restoreFocus.current) {
      restoreFocus.current = false;
      editRef.current?.focus();
    }
  }, [editing]);

  const keyHandler = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      finish(true); // cancel: the draft is dropped
    }
  };

  if (!editing) {
    const activate = (event: { target: EventTarget }) => {
      if (readOnly) return;
      if ((event.target as HTMLElement).closest('a')) return; // links keep working
      // Selecting text is not editing (a double click selects a word on its own: allowed there).
      if (activation === 'click' && window.getSelection()?.toString()) return;
      open();
    };
    return (
      <div className={styles.view}>
        <div
          className={styles.text}
          data-testid="editable-markdown"
          title={
            readOnly
              ? undefined
              : t(activation === 'click' ? 'content.clickToEdit' : 'content.doubleClickToEdit')
          }
          onClick={activation === 'click' ? activate : undefined}
          onDoubleClick={activation === 'dblclick' ? activate : undefined}
        >
          {value.trim() ? (
            <Markdown>{value}</Markdown>
          ) : (
            <p className={styles.empty}>{emptyText}</p>
          )}
        </div>
        {editButton && !readOnly && (
          <Button
            ref={editRef}
            size="sm"
            variant="ghost"
            className={styles.editButton}
            onClick={open}
          >
            {editLabel}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={styles.form}
      data-testid="markdown-editing"
      onBlur={(event) => {
        // Tabbing out of the whole editor saves; clicks are handled by pointerdown above.
        const next = event.relatedTarget;
        if (next instanceof Node && !event.currentTarget.contains(next)) void save();
      }}
    >
      <MarkdownEditor
        label={label}
        value={draft}
        onChange={setDraft}
        maxLength={maxLength}
        rows={Math.min(Math.max(draft.split('\n').length + 1, 5), 20)}
        autoFocus
        onKeyDown={keyHandler}
      />
      <p className={styles.hint} aria-live="polite">
        {pending
          ? t('content.saving')
          : t(touch ? 'content.autosaveHintTouch' : 'content.autosaveHint')}
      </p>
      <FormError error={error} />
      {error !== null && (
        <div className={styles.actions}>
          <Button
            size="sm"
            variant="primary"
            disabled={pending}
            onClick={() => {
              void save();
            }}
          >
            {t('content.retry')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              finish(true);
            }}
          >
            {t('content.discard')}
          </Button>
        </div>
      )}
      {conflict && (
        <div className={styles.conflict} role="alert">
          <p>{t('content.conflict')}</p>
          <div className={styles.actions}>
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                void save(true);
              }}
            >
              {t('content.keepMine')}
            </Button>
            <Button
              size="sm"
              onClick={() => {
                finish(true);
              }}
            >
              {t('content.useLatest')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
